import { useEffect, useState } from "react";
import { BotaoContorno } from "../components/ui/BotaoAcao";
import { BotaoTopoPrimario } from "../components/ui/BotoesTopo";
import { CampoCodigoOtp } from "../components/ui/CampoCodigoOtp";
import { ModalCadastro } from "../components/ui/ModalCadastro";
import { confirmarAtivacao2FA, iniciarConfiguracao2FA } from "../api/usuario";
import { useIdioma } from "../i18n/IdiomaProvider";
import { mostrarToast, mostrarToastErro } from "../utils/toast";
import styles from "./Configurar2FAModal.module.css";

interface Configurar2FAModalProps {
  aberto: boolean;
  onFechar: () => void;
  onHabilitado: () => void;
}

export function Configurar2FAModal({ aberto, onFechar, onHabilitado }: Configurar2FAModalProps) {
  const { traduzir } = useIdioma();
  const [carregando, setCarregando] = useState(false);
  const [qrCode, setQrCode] = useState("");
  const [secret, setSecret] = useState("");
  const [mostrarChave, setMostrarChave] = useState(false);
  const [codigo, setCodigo] = useState("");
  const [verificando, setVerificando] = useState(false);
  const [erro, setErro] = useState("");

  useEffect(() => {
    if (!aberto) return;
    setCodigo("");
    setErro("");
    setMostrarChave(false);
    setQrCode("");
    setSecret("");
    setCarregando(true);
    iniciarConfiguracao2FA()
      .then((resposta) => {
        if (resposta.sucesso === false) throw new Error(resposta.erro || "Não foi possível iniciar a configuração.");
        setQrCode(resposta.qrCode || "");
        setSecret(resposta.secret || "");
      })
      .catch((erroCarregar) => {
        setErro(erroCarregar instanceof Error ? erroCarregar.message : "Não foi possível iniciar a configuração.");
      })
      .finally(() => setCarregando(false));
  }, [aberto]);

  async function verificar() {
    if (codigo.length !== 6) {
      setErro("Digite os 6 dígitos do código.");
      return;
    }
    setVerificando(true);
    setErro("");
    try {
      const resposta = await confirmarAtivacao2FA(codigo);
      if (resposta.sucesso === false) throw new Error(resposta.erro || "Código inválido.");
      mostrarToast(traduzir("Autenticação de dois fatores habilitada com sucesso."), "sucesso");
      onHabilitado();
      onFechar();
    } catch (erroVerificar) {
      const mensagem = erroVerificar instanceof Error ? erroVerificar.message : "Código inválido.";
      setErro(mensagem);
      mostrarToastErro(traduzir(mensagem));
    } finally {
      setVerificando(false);
    }
  }

  return (
    <ModalCadastro
      aberto={aberto}
      onFechar={() => !verificando && onFechar()}
      titulo={traduzir("Configurar autenticação de dois fatores")}
      subtitulo={traduzir("Adicione uma camada extra de segurança à sua conta.")}
      largura={520}
      rodape={
        <div className={styles.rodape}>
          <BotaoContorno estilo="topo" onClick={onFechar} disabled={verificando}>
            {traduzir("Cancelar")}
          </BotaoContorno>
          <BotaoTopoPrimario
            onClick={() => void verificar()}
            carregando={verificando}
            disabled={codigo.length !== 6 || carregando}
          >
            {traduzir("Verificar e habilitar")}
          </BotaoTopoPrimario>
        </div>
      }
    >
      <div className={styles.corpo}>
        {erro && <div className={styles.erro}>{traduzir(erro)}</div>}

        <div className={styles.etapa}>
          <strong>{traduzir("1. Escaneie este código com seu aplicativo autenticador")}</strong>
          <p className={styles.texto}>
            {traduzir("Abra o aplicativo autenticador (Google Authenticator, Microsoft Authenticator ou Authy), toque em adicionar e aponte sua câmera para este código.")}
          </p>
        </div>

        <div className={styles.qrWrap}>
          {carregando ? (
            <div className={styles.qrPlaceholder}>{traduzir("Gerando código...")}</div>
          ) : qrCode ? (
            <img className={styles.qr} src={qrCode} alt={traduzir("Código QR para configurar o autenticador")} />
          ) : (
            <div className={styles.qrPlaceholder}>{traduzir("Não foi possível gerar o código.")}</div>
          )}
        </div>

        <button type="button" className={styles.chaveToggle} onClick={() => setMostrarChave((v) => !v)}>
          {traduzir(mostrarChave ? "Ocultar chave manual" : "Não consegue escanear? Digite a chave manualmente")}
        </button>
        {mostrarChave && secret && (
          <code className={styles.chaveManual} data-i18n-db="true">{secret}</code>
        )}

        <div className={styles.etapa}>
          <strong>{traduzir("2. Digite o código de verificação para confirmar")}</strong>
        </div>
        <CampoCodigoOtp valor={codigo} onChange={setCodigo} disabled={verificando || carregando} />
      </div>
    </ModalCadastro>
  );
}
